import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { discoverLives } from '../api/lives';
import LiveCard from '../components/LiveCard';
import CreateLiveDialog from '../components/CreateLiveDialog';
import { Button } from '../components/ui/button';
import { Radio, Plus, Calendar, History } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { useAuth } from '../context/AuthContext';
import ReadOnlyNotice from '../components/ReadOnlyNotice';
import { canWrite } from '../lib/canWrite';

const Section = ({ title, icon: Icon, children, empty, accent }) => (
  <section className="mt-6 first:mt-0">
    <div className="mb-2 flex items-center gap-2">
      <span className={`flex h-7 w-7 items-center justify-center rounded-lg ${accent}`}>
        <Icon className="h-4 w-4" />
      </span>
      <h2 className="text-base font-semibold">{title}</h2>
    </div>
    {React.Children.count(children) > 0 ? (
      <div className="flex flex-col gap-2">{children}</div>
    ) : empty ? (
      <div className="rounded-xl border border-dashed border-border bg-card px-4 py-6 text-center text-sm text-muted-foreground">
        {empty}
      </div>
    ) : null}
  </section>
);

const LivesPage = () => {
  const { t } = useTranslation('lives');
  const [data, setData] = useState({ live_now: [], upcoming: [], past: [] });
  const [loading, setLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const writable = canWrite(user);

  const load = async () => {
    try {
      const d = await discoverLives();
      setData(d);
    } catch (_e) {
      toast({ title: t('page.load_error'), variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const interval = setInterval(load, 15000);
    const onChanged = () => load();
    window.addEventListener('nymia:data-changed', onChanged);
    return () => {
      clearInterval(interval);
      window.removeEventListener('nymia:data-changed', onChanged);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div className="mx-auto max-w-[800px] px-4 pt-6 pb-24 md:pt-10">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-semibold">
            <Radio className="h-6 w-6 text-red-500" /> {t('page.title')}
          </h1>
          <p className="text-sm text-muted-foreground">{t('page.subtitle')}</p>
        </div>
        <Button
          onClick={() => writable
            ? setCreateOpen(true)
            : toast({ title: t('page.verify_email_title'), description: t('page.verify_email_desc'), variant: 'destructive' })}
          disabled={!writable}
          className="h-9 bg-red-500 hover:bg-red-600 disabled:opacity-50"
          data-testid="lives-create-btn"
        >
          <Plus className="mr-1.5 h-4 w-4" /> {t('page.schedule_cta')}
        </Button>
      </div>

      <ReadOnlyNotice action={t('page.read_only_action')} className="mb-4" />

      {loading ? (
        <div className="flex justify-center py-10">
          <div className="h-7 w-7 animate-spin rounded-full border-2 border-border border-t-neutral-800" />
        </div>
      ) : (
        <>
          <Section
            title={t('sections.live_now')}
            icon={Radio}
            empty={t('sections.live_now_empty')}
            accent="bg-red-500/20 text-red-300"
          >
            {data.live_now.map((l) => <LiveCard key={l.id} live={l} />)}
          </Section>
          <Section
            title={t('sections.upcoming')}
            icon={Calendar}
            empty={t('sections.upcoming_empty')}
            accent="bg-blue-500/20 text-blue-300"
          >
            {data.upcoming.map((l) => <LiveCard key={l.id} live={l} />)}
          </Section>
          {data.past.length > 0 && (
            <Section
              title={t('sections.past')}
              icon={History}
              empty=""
              accent="bg-secondary text-foreground"
            >
              {data.past.map((l) => <LiveCard key={l.id} live={l} compact />)}
            </Section>
          )}
        </>
      )}

      <CreateLiveDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        onCreated={() => load()}
      />
    </div>
  );
};

export default LivesPage;
